import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react'
import { buildingFilesService } from '../services'
import type { LandFile, FileCategory } from '@popety-io/types'

interface BuildingFilesContextType {
  // Data
  files: LandFile[]
  loading: boolean
  error: string | null
  total: number
  categories: { category: FileCategory; count: number }[]

  // Filters
  searchTerm: string
  setSearchTerm: (term: string) => void
  activeCategory: FileCategory | 'all'
  setActiveCategory: (category: FileCategory | 'all') => void

  // Actions
  fetchFiles: (buildingId: string) => Promise<void>
  refreshFiles: () => Promise<void>
  uploadFile: (
    buildingId: string,
    file: File,
    description?: string
  ) => Promise<void>
  deleteFile: (fileId: string) => Promise<void>
  downloadFile: (fileId: string) => Promise<void>

  // Current building ID
  currentBuildingId: string | null
}

const BuildingFilesContext = createContext<
  BuildingFilesContextType | undefined
>(undefined)

export const BuildingFilesProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Core state
  const [files, setFiles] = useState<LandFile[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [total, setTotal] = useState(0)
  const [categories, setCategories] = useState<
    { category: FileCategory; count: number }[]
  >([])
  const [currentBuildingId, setCurrentBuildingId] = useState<string | null>(
    null
  )

  // Filter state
  const [searchTerm, setSearchTerm] = useState('')
  const [activeCategory, setActiveCategory] = useState<FileCategory | 'all'>(
    'all'
  )

  // Fetch files for a specific building
  const fetchFiles = useCallback(async (buildingId: string) => {
    try {
      setLoading(true)
      setError(null)
      setCurrentBuildingId(buildingId)

      const response = await buildingFilesService.getByBuildingId(buildingId)
      setFiles(response.files)
      setTotal(response.total)
      setCategories(response.categories)
    } catch (err) {
      console.error('Failed to fetch files:', err)
      setError('Failed to load files')
      setFiles([])
      setTotal(0)
      setCategories([])
    } finally {
      setLoading(false)
    }
  }, [])

  // Refresh files for current building
  const refreshFiles = useCallback(async () => {
    if (currentBuildingId) {
      await fetchFiles(currentBuildingId)
    }
  }, [currentBuildingId, fetchFiles])

  // Upload a new file
  const uploadFile = useCallback(
    async (buildingId: string, file: File, description?: string) => {
      try {
        setLoading(true)
        setError(null)

        await buildingFilesService.uploadFile(buildingId, file, description)

        // Refresh files after upload
        await refreshFiles()
      } catch (err) {
        console.error('Failed to upload file:', err)
        setError('Failed to upload file')
        throw err
      } finally {
        setLoading(false)
      }
    },
    [refreshFiles]
  )

  // Delete a file
  const deleteFile = useCallback(
    async (fileId: string) => {
      try {
        setLoading(true)
        setError(null)

        await buildingFilesService.deleteFile(fileId)

        // Refresh files after deletion
        await refreshFiles()
      } catch (err) {
        console.error('Failed to delete file:', err)
        setError('Failed to delete file')
        throw err
      } finally {
        setLoading(false)
      }
    },
    [refreshFiles]
  )

  // Download a file
  const downloadFile = useCallback(async (fileId: string) => {
    try {
      await buildingFilesService.downloadFile(fileId)
    } catch (err) {
      console.error('Failed to download file:', err)
      setError('Failed to download file')
      throw err
    }
  }, [])

  // Reset filters when building changes
  useEffect(() => {
    setSearchTerm('')
    setActiveCategory('all')
  }, [currentBuildingId])

  const value: BuildingFilesContextType = {
    // Data
    files,
    loading,
    error,
    total,
    categories,

    // Filters
    searchTerm,
    setSearchTerm,
    activeCategory,
    setActiveCategory,

    // Actions
    fetchFiles,
    refreshFiles,
    uploadFile,
    deleteFile,
    downloadFile,

    // Current building ID
    currentBuildingId,
  }

  return (
    <BuildingFilesContext.Provider value={value}>
      {children}
    </BuildingFilesContext.Provider>
  )
}

export const useBuildingFiles = () => {
  const context = useContext(BuildingFilesContext)
  if (context === undefined) {
    throw new Error(
      'useBuildingFiles must be used within a BuildingFilesProvider'
    )
  }
  return context
}
